import React from 'react';
import { Link } from 'react-router-dom';
import { Paintbrush, CheckCircle2, Clock, Shield, Palette, ArrowRight } from 'lucide-react';
import BeforeAfterGallery from '../../components/BeforeAfterGallery';
import WorkGallery from '../../components/WorkGallery';
import Reviews from '../../components/Reviews';

const ScratchPaintRepair = () => {
  const beforeAfterPairs = [
    { before: '/siteimages/Before 1.jpg', after: '/siteimages/after 1.jpg' },
    { before: '/siteimages/before 2.jpg', after: '/siteimages/after 2.jpg' },
    { before: '/siteimages/before 3.jpg', after: '/siteimages/after 3.jpg' },
    { before: '/siteimages/before 4.jpg', after: '/siteimages/after 4.jpg' },
  ];

  const workImages = [
    '/siteimages/image_0001.jpg',
    '/siteimages/image_0012.jpg',
    '/siteimages/image_0020.jpg',
    '/siteimages/image_0023.jpg',
    '/siteimages/image_0027.jpg',
    '/siteimages/image_0029.jpg',
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-dark-navy to-dark-teal text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Paintbrush className="w-12 h-12 text-bmw-blue" />
            <h1 className="text-4xl md:text-5xl font-heading font-bold">
              Scratch & Paint Repair
            </h1>
          </div>
          <p className="text-lg md:text-xl text-soft-grey max-w-3xl">
            Professional scratch repair and paint restoration services with perfect color matching and factory-finish quality.
          </p>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-heading font-bold text-dark-navy mb-6">
                Expert <span className="text-bmw-blue">Paint Repair</span>
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Scratches, scuffs, and paint damage can significantly affect your vehicle's appearance and value. 
                Our expert technicians specialize in scratch repair and paint restoration, using advanced color-matching 
                technology to ensure a perfect finish that blends seamlessly with your vehicle's original paint.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Whether it's minor surface scratches, deep paint damage, or complete panel resprays, we have the 
                expertise and equipment to restore your vehicle to showroom condition. We use premium automotive paints 
                and clear coats that match OEM specifications for long-lasting results.
              </p>

              <div className="bg-bmw-blue bg-opacity-10 border-l-4 border-bmw-blue p-6 rounded-r-lg">
                <h3 className="font-bold text-dark-navy mb-2 flex items-center gap-2">
                  <Palette className="w-5 h-5 text-bmw-blue" />
                  Perfect Color Match Guaranteed
                </h3>
                <p className="text-gray-600">
                  Our computerized color-matching system analyzes your vehicle's exact paint code and accounts for 
                  age-related fading to achieve a flawless, undetectable repair.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-off-white p-6 rounded-lg">
                <h3 className="text-xl font-bold text-dark-navy mb-4">What We Repair</h3>
                <ul className="space-y-3">
                  {[
                    'Minor scratches and scuffs',
                    'Deep paint scratches',
                    'Key scratches and vandalism damage',
                    'Paint chips and stone chips',
                    'Parking lot scratches',
                    'Clearcoat damage',
                    'Paint oxidation and fading',
                    'Multi-panel paint repairs',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-bmw-blue flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-racing-red bg-opacity-10 border border-racing-red p-6 rounded-lg">
                <h3 className="font-bold text-dark-navy mb-3">Quick Estimate Available</h3>
                <p className="text-gray-600 mb-4">
                  Get a free, no-obligation quote for your scratch repair. Contact us today!
                </p>
                <Link 
                  to="/contact" 
                  className="inline-flex items-center gap-2 bg-racing-red text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition"
                >
                  Get Free Quote
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-off-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-heading font-bold text-dark-navy mb-12 text-center">
            Our <span className="text-bmw-blue">Repair Process</span>
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Assessment',
                description: 'Thorough inspection and damage assessment to determine the best repair approach.',
              },
              {
                step: '02',
                title: 'Preparation',
                description: 'Surface preparation including cleaning, sanding, and primer application as needed.',
              },
              {
                step: '03',
                title: 'Color Matching',
                description: 'Precise color matching using computerized technology for perfect paint blending.',
              },
              {
                step: '04',
                title: 'Paint & Finish',
                description: 'Expert paint application and clear coat finishing for a flawless, durable result.',
              },
            ].map((process, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-5xl font-bold text-bmw-blue opacity-20 mb-3">{process.step}</div>
                <h3 className="text-xl font-bold text-dark-navy mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-dark-navy text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Fast Turnaround</h3>
              <p className="text-soft-grey">
                Most scratch repairs completed within 1-3 days, depending on damage extent.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Quality Guarantee</h3>
              <p className="text-soft-grey">
                All paint repairs backed by our comprehensive workmanship guarantee.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Palette className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Premium Materials</h3>
              <p className="text-soft-grey">
                We use only high-quality automotive paints and clear coats for lasting results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Before & After Gallery */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-4">
            <p className="text-bmw-blue font-semibold text-sm uppercase tracking-wide mb-2">Our Results</p>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-navy mb-4">
              Scratch Repair <span className="text-bmw-blue">Transformations</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-12">
              See the quality of our scratch and paint repair work. Hover to view before and after.
            </p>
          </div>
          <BeforeAfterGallery beforeAfterPairs={beforeAfterPairs} title="" />
        </div>
      </section>

      {/* Work Gallery */}
      <section className="py-16 bg-off-white">
        <div className="container mx-auto px-4">
          <WorkGallery images={workImages} title="Recent Paint Repairs" />
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <Reviews limit={3} showTrustpilot={false} />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-bmw-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Ready to Fix Those Scratches?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Contact us today to discuss your scratch repair needs. We'll provide a transparent quote and timeline.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-racing-red text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition shadow-lg inline-flex items-center justify-center gap-2"
            >
              Get Free Quote
              <ArrowRight />
            </Link>
            <a 
              href="tel:08000452446" 
              className="bg-white text-bmw-blue px-8 py-4 rounded-lg font-semibold hover:bg-off-white transition shadow-lg"
            >
              Call: 0800 045 2446
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ScratchPaintRepair;
